import type { Country, DateRange, DepartureCityType, NightsRange, DefaultCriteriaIndex } from '@/shared/model';
import type { Selection } from '../model/types';

export interface ResolvedCriteria {
  nights?: NightsRange;
  dateRange?: DateRange;
}

export function resolveDefaultCriteria(
  index: DefaultCriteriaIndex,
  countryId: number | undefined,
  cityType?: DepartureCityType,
): ResolvedCriteria {
  if (countryId === undefined || !Number.isFinite(countryId)) {
    return {};
  }
  const country = index[countryId];
  if (!country) {
    return {};
  }

  if (cityType === 'capital') {
    const block = country.capital;
    if (!block) {
      return {};
    }
    return { nights: block.nights, dateRange: block.dateRange };
  }

  return country.region?.nights ? { nights: country.region.nights, dateRange: country.region.dateRange } : {};
}

export function resolveInitialDateFrom(
  index: DefaultCriteriaIndex,
  countryId: number | undefined,
  cityType?: DepartureCityType,
): DateRange | undefined {
  if (countryId === undefined || !Number.isFinite(countryId)) {
    return undefined;
  }
  const country = index[countryId];
  if (!country) {
    return undefined;
  }
  const primary = cityType === 'region' ? country.region : country.capital;
  const fallback = cityType === 'region' ? country.capital : country.region;
  return primary?.dateRange ?? fallback?.dateRange;
}

export function resolveSourceCountryId(destination: Selection | null, countries: Country[]): number | undefined {
  if (destination && destination.kind !== 'none') {
    return destination.country.id;
  }

  if (countries.length === 0) {
    return undefined;
  }

  const best = countries.slice(1).reduce<Country>((acc, c) => {
    const ap = acc.position ?? Number.POSITIVE_INFINITY;
    const cp = c.position ?? Number.POSITIVE_INFINITY;
    return cp < ap ? c : acc;
  }, countries[0]);
  return best.id;
}

export function nightsEqual(a: NightsRange | null, b: NightsRange | null): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  return a.from === b.from && a.to === b.to;
}

export function dateFromEqual(a: DateRange | null, b: DateRange | null): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  return a.from === b.from && a.to === b.to && a.daysFlexible === b.daysFlexible;
}
